import { Box } from "@chakra-ui/react";
import { useTranslation } from "hooks/useTranslation";
import { MdRefresh } from "react-icons/md";
import { Button } from "./button";

/**
 * Canonical "Refresh" button — drop into any page header that displays
 * DB-backed data which can change while the user is on the page.
 *
 * Visual signature (so users learn to recognise it instantly):
 *  - Always intent="secondary" (matches every other secondary action)
 *  - Always uses the <MdRefresh> icon
 *  - Always labelled with t('app.common.refresh') (override via `label` for
 *    page-specific phrasing like "Refresh logs")
 *  - Icon rotates 180° on hover — small but distinctive cue
 *  - Loading state spins the icon continuously
 *
 * Use `iconOnly` for tight horizontal space (table toolbars, card headers).
 */

interface RefreshButtonProps {
  onClick: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  iconOnly?: boolean;
  /** Override label. Defaults to t('app.common.refresh'). */
  label?: string;
}

export const RefreshButton = ({
  onClick,
  loading,
  disabled,
  size = "md",
  iconOnly,
  label,
}: RefreshButtonProps) => {
  const { t } = useTranslation();
  const text = label ?? t("app.common.refresh");

  return (
    <Button
      onClick={onClick}
      intent="secondary"
      size={size}
      loading={loading}
      loadingText={iconOnly ? undefined : text}
      disabled={disabled}
      title={iconOnly ? text : undefined}
      aria-label={text}
      css={{
        "& > svg, & .refresh-icon": {
          transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        },
        "&:hover:not([disabled]) .refresh-icon": {
          transform: "rotate(180deg)",
        },
        "&[data-loading] .refresh-icon": {
          animation: "spin 0.9s linear infinite",
        },
      }}
    >
      <Box as="span" className="refresh-icon" display="inline-flex">
        <MdRefresh />
      </Box>
      {!iconOnly && text}
    </Button>
  );
};
